import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/* Offer pages close immediately during navigation so bfcache cannot retain an open cart. */

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;
  #openFrame = 0;
  #closeSequence = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openFrame) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = 0;
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      dialog.addEventListener('cancel', this.#handleCancel);
      dialog.addEventListener('close', this.#handleClose);
    });
  }

  /**
   * Closes the dialog.
   * @param {object} [options]
   * @param {boolean} [options.immediate=false] - Skip animation for page navigation.
   */
  closeDialog = async ({ immediate = false } = {}) => {
    const { dialog } = this.refs;
    const closeSequence = ++this.#closeSequence;

    cancelAnimationFrame(this.#openFrame);
    this.#openFrame = 0;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (!immediate) {
      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });
    }

    if (closeSequence !== this.#closeSequence) return;

    this.#finishClose();
  };

  /**
   * Releases the body, closes the dialog and announces the close.
   */
  #finishClose() {
    const { dialog } = this.refs;

    // Detach first: dialog.close() queues a native close event for #handleClose.
    dialog.removeEventListener('cancel', this.#handleCancel);
    dialog.removeEventListener('close', this.#handleClose);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * TAO: routes the browser's own close requests through closeDialog(). The browser can
   * close a modal dialog by itself: Escape while focus is outside the component (e.g. on
   * <body> after the cart re-renders), the Android back gesture, requestClose(). Horizon
   * (3.2.1, still in 4.3.0) only releases the body in closeDialog(), so the page stayed
   * locked. Re-apply this and #handleClose on Horizon upgrades.
   *
   * @param {Event} event - The cancel event.
   */
  #handleCancel = (event) => {
    // Chrome makes cancel non-cancelable without a fresh user activation. The browser then
    // closes the dialog itself, and the closeDialog() started here still releases the body.
    event.preventDefault();

    if (this.refs.dialog.classList.contains('dialog-closing')) return;

    this.closeDialog();
  };

  /**
   * Finishes any close that bypassed closeDialog().
   */
  #handleClose = () => {
    const { dialog } = this.refs;

    // Reopened since, or closeDialog() is already finishing this close.
    if (dialog.open || dialog.classList.contains('dialog-closing')) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#finishClose();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
          document.documentElement.classList.add('scroll-lock');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
          document.documentElement.classList.remove('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
